"use client";

import styles from './styles.module.css';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

export default function ApproachSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLElement | null>(null);
  const [highlightCount, setHighlightCount] = useState<number>(0);

  // Text chunks around icons; will be split into tokens
  const chunk1 = 'Полный цикл — от идеи';
  const chunk2 = 'и дизайна до готового продукта. Все — в одних руках.';
  const chunk3 = 'Прозрачный процесс, понятные сроки и личный кабинет, где вы в любой момент видите статус проекта';

  // Split text into tokens preserving spaces
  const splitWithSpaces = (text: string) => text.split(/(\s+)/);
  const tokens: Array<{ type: 'word'; text: string } | { type: 'space'; text: string } | { type: 'icon'; key: string } > = [];
  for (const t of splitWithSpaces(chunk1)) tokens.push(/\s+/.test(t) ? { type: 'space', text: t } : { type: 'word', text: t });
  tokens.push({ type: 'icon', key: 'flash' });
  tokens.push({ type: 'space', text: ' ' });
  for (const t of splitWithSpaces(chunk2)) tokens.push(/\s+/.test(t) ? { type: 'space', text: t } : { type: 'word', text: t });
  tokens.push({ type: 'space', text: ' ' });
  tokens.push({ type: 'icon', key: 'stone' });
  tokens.push({ type: 'space', text: ' ' });
  for (const t of splitWithSpaces(chunk3)) tokens.push(/\s+/.test(t) ? { type: 'space', text: t } : { type: 'word', text: t });
  tokens.push({ type: 'space', text: ' ' });
  tokens.push({ type: 'icon', key: 'puzzle' });

  const totalWords = tokens.reduce((acc, t) => acc + (t.type === 'word' ? 1 : 0), 0);

  // Derive highlight count from section progress based on total words
  const [progressInternal, setProgressInternal] = useState(0);
  useEffect(() => {
    function onScroll() {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const viewportH = window.innerHeight;
      // Use wrapper-based sticky progress for all breakpoints
      const pinTop = viewportH * 0.20; // desktop sticky top
      const denom = Math.max(1, rect.height - viewportH + pinTop);
      const tRaw = (pinTop - rect.top) / denom; // 0 when top reaches pinTop, 1 when bottom reaches viewport bottom
      const t = Math.max(0, Math.min(1, tRaw));
      setProgressInternal(t);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll as EventListener);
      window.removeEventListener('resize', onScroll as EventListener);
    };
  }, []);

  const computedHighlightCount = Math.floor(progressInternal * totalWords);
  if (computedHighlightCount !== highlightCount) {
    // avoid excessive renders by batching
    if (typeof window !== 'undefined') {
      // schedule microtask to update state
      Promise.resolve().then(() => setHighlightCount(computedHighlightCount));
    }
  }

  return (
    <section className={styles.pinWrapper} ref={wrapperRef as unknown as React.RefObject<HTMLElement>}>
      <div className={styles.stickyContainer}>
        <section id="approach" className={styles.approachSection} ref={sectionRef}>
          <div className={styles.leftBlock}>
            <h2 className={styles.title}>Подход</h2>
          </div>

          <div className={styles.rightBlock}>
            <div className={styles.content}>
              <p className={styles.text}>
                {tokens.map((t, idx) => {
                  if (t.type === 'space') return t.text;
                  if (t.type === 'icon') {
                    const wordsBefore = tokens.slice(0, idx).reduce((acc, x) => acc + (x.type === 'word' ? 1 : 0), 0);
                    const iconActive = highlightCount >= wordsBefore;
                    const iconClass = iconActive ? `${styles.icon} ${styles.iconActive}` : `${styles.icon} ${styles.iconInactive}`;
                    if (t.key === 'flash') return <Image key={`icon-${idx}`} src="/assets/icons/flash.svg" alt="Flash" width={60} height={60} className={iconClass} />;
                    if (t.key === 'stone') return <Image key={`icon-${idx}`} src="/assets/icons/stone.svg" alt="Stone" width={60} height={60} className={iconClass} />;
                    return <Image key={`icon-${idx}`} src="/assets/icons/puzzle.svg" alt="Puzzle" width={60} height={60} className={iconClass} />;
                  }
                  const wordIndex = tokens.slice(0, idx + 1).reduce((acc, x) => acc + (x.type === 'word' ? 1 : 0), 0);
                  const isHighlighted = wordIndex <= highlightCount;
                  const color = isHighlighted ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.4)';
                  return (
                    <span key={`w-${idx}`} className={styles.word} style={{ color }}>{t.text}</span>
                  );
                })}
              </p>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
}